/**
 * 고객사 목록 관리 훅
 * 
 * 고객사 목록을 불러오고 관리하는 로직을 재사용 가능하게 제공
 */

import { useState, useCallback, useEffect } from 'react';
import { adminAPI } from '@/services/api';
import { Customer } from '@/types';
import { handleAPIError } from '@/utils/errorUtils';

export interface UseCustomersOptions {
  autoFetch?: boolean;  // 자동으로 fetch 실행 (기본: true)
  limit?: number;       // 가져올 고객사 수 (기본: 1000)
}

export interface UseCustomersReturn {
  customers: Customer[];
  loading: boolean;
  error: string;
  fetchCustomers: () => Promise<void>;
  refetch: () => Promise<void>;
  setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>;
}

/**
 * 고객사 목록 관리 훅
 * 
 * @param options - 옵션
 * @returns 고객사 목록과 관리 함수들
 * 
 * @example
 * const { customers, loading, error, refetch } = useCustomers();
 * 
 * // 자동 fetch 비활성화
 * const { customers, fetchCustomers } = useCustomers({ autoFetch: false });
 * useEffect(() => {
 *   if (shouldFetch) {
 *     fetchCustomers();
 *   }
 * }, [shouldFetch]);
 */
export const useCustomers = (options: UseCustomersOptions = {}): UseCustomersReturn => {
  const { autoFetch = true, limit = 1000 } = options;
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminAPI.getActiveCustomers(0, limit);
      setCustomers(response.customers || []);
    } catch (err: any) {
      handleAPIError(err, '고객사 목록을 불러오는데 실패했습니다.', setError);
    } finally {
      setLoading(false);
    }
  }, [limit]);
  
  // autoFetch가 true이면 마운트 시 자동으로 fetch
  useEffect(() => {
    if (autoFetch) {
      fetchCustomers();
    }
  }, [autoFetch, fetchCustomers]);
  
  return {
    customers,
    loading,
    error,
    fetchCustomers,
    refetch: fetchCustomers,  // 별칭
    setCustomers,
  };
};

/**
 * 고객사 ID를 문자열로 변환하여 useSearchFilters와 함께 사용
 * 
 * @param defaultValue - 기본값 ('all', 'none', 또는 고객사 ID)
 * @returns 고객사 ID 필터 관리 객체
 * 
 * @example
 * const { 
 *   customerId, 
 *   customerIdStr, 
 *   setCustomerId, 
 *   customers 
 * } = useCustomerFilter('all');
 */
export const useCustomerFilter = (defaultValue: string = 'all') => {
  const [customerIdStr, setCustomerIdStr] = useState(defaultValue);
  const { customers, loading, error } = useCustomers();
  
  const customerId = 
    customerIdStr === 'none' ? null :
    customerIdStr === 'all' ? undefined :
    parseInt(customerIdStr, 10);
  
  const setCustomerId = (value: number | null | undefined) => {
    if (value === null) {
      setCustomerIdStr('none');
    } else if (value === undefined) {
      setCustomerIdStr('all');
    } else {
      setCustomerIdStr(String(value));
    }
  };
  
  return {
    customerId,
    customerIdStr,
    setCustomerId,
    setCustomerIdStr,
    customers,
    loading,
    error,
  };
};

